import { useCallback, useRef, useState } from "react";
import type { DataNode } from "antd/es/tree";

/**
 * Drop position:
 *  -1 = before the node (gap above)
 *   0 = inside the node (as child, for folders only)
 *   1 = after the node (gap below)
 */
export type DropPosition = -1 | 0 | 1;

export interface DragState {
  /** 拖拽中的节点 key */
  dragNodeKey: string | null;
  /** 当前悬停的目标节点 key */
  dropNodeKey: string | null;
  /** drop 位置 */
  dropPosition: DropPosition;
}

export interface UseFolderDragOptions {
  /** 是否启用拖拽（'file' | 'folder' | 'all'） */
  dragMode: "file" | "folder" | "all" | false;
  /** 拖拽完成回调 */
  onDrop?: (dragNode: DataNode, dropNode: DataNode, dropPosition: DropPosition) => void;
  /** 拖拽进入节点时回调（用于自动展开） */
  onDragEnterExpand?: (nodeKey: string) => void;
}

export interface UseFolderDragReturn {
  dragState: DragState;
  /** 绑定到 draggable 元素的 onDragStart */
  onDragStart: (e: React.DragEvent, node: DataNode) => void;
  /** 绑定到 draggable 元素的 onDragEnd */
  onDragEnd: (e: React.DragEvent) => void;
  /** 绑定到每个树节点容器的 onDragEnter */
  onItemDragEnter: (e: React.DragEvent, node: DataNode) => void;
  /** 绑定到每个树节点容器的 onDragOver */
  onItemDragOver: (e: React.DragEvent, node: DataNode) => void;
  /** 绑定到每个树节点容器的 onDragLeave */
  onItemDragLeave: (e: React.DragEvent) => void;
  /** 绑定到每个树节点容器的 onDrop */
  onItemDrop: (e: React.DragEvent, node: DataNode) => void;
  /** 绑定到树内容容器（directoryTreeContent）的 onDragOver，处理拖到空白区域 */
  onContainerDragOver: (e: React.DragEvent) => void;
  /** 绑定到树内容容器（directoryTreeContent）的 onDrop，处理拖到空白区域 */
  onContainerDrop: (e: React.DragEvent) => void;
  /** 清除拖拽状态 */
  clearDragState: () => void;
  /** 是否正在拖拽中 */
  isDragging: boolean;
  /** 判断节点是否可拖拽 */
  canDragNode: (node: DataNode & { _isTempNode?: boolean }) => boolean;
}

/**
 * 根据鼠标 Y 坐标和节点 DOM rect 计算 dropPosition
 */
function calcDropPositionByRect(
  clientY: number,
  rect: DOMRect,
  isFolder: boolean,
): { dropPosition: DropPosition; insideThreshold: boolean } {
  const top = rect.top;
  const height = rect.height;
  const relativeY = clientY - top;

  // 上半部分 1/3：before
  if (relativeY < height / 3) {
    return { dropPosition: -1, insideThreshold: false };
  }
  // 下半部分 1/3：after
  if (relativeY > (height * 2) / 3) {
    return { dropPosition: 1, insideThreshold: false };
  }
  // 中间 1/3：如果是文件夹，drop inside；如果是文件，drop after
  if (isFolder) {
    return { dropPosition: 0, insideThreshold: true };
  }
  return { dropPosition: 1, insideThreshold: false };
}

const INDICATOR_BEFORE_CLASS = "folder-tree-drop-before";
const INDICATOR_INSIDE_CLASS = "folder-tree-drop-inside";
const INDICATOR_AFTER_CLASS = "folder-tree-drop-after";

const INDICATOR_CLASSES = [INDICATOR_BEFORE_CLASS, INDICATOR_INSIDE_CLASS, INDICATOR_AFTER_CLASS];

/** 从 data-node-key 元素找到对应的 ant-tree-treenode */
function getTreeNodeEl(dataNodeKeyEl: HTMLElement): HTMLElement | null {
  return dataNodeKeyEl.closest(".ant-tree-treenode") as HTMLElement | null;
}

/** 清除所有 treenode 上的 indicator class */
function clearAllTreenodeIndicators() {
  document.querySelectorAll(".ant-tree-treenode").forEach((el) => {
    el.classList.remove(...INDICATOR_CLASSES);
  });
}

export function useFolderDrag(options: UseFolderDragOptions): UseFolderDragReturn {
  const { dragMode, onDrop, onDragEnterExpand } = options;

  const [dragState, setDragState] = useState<DragState>({
    dragNodeKey: null,
    dropNodeKey: null,
    dropPosition: 0,
  });
  const [isDragging, setIsDragging] = useState(false);

  // 用 ref 追踪拖拽中数据，避免闭包问题
  const dragNodeRef = useRef<DataNode | null>(null);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevDropTargetRef = useRef<string | null>(null);

  const canDragNode = useCallback(
    (node: DataNode & { _isTempNode?: boolean }) => {
      if (node._isTempNode) return false;
      if (dragMode === "file") return !!node.isLeaf;
      if (dragMode === "folder") return !node.isLeaf;
      return true; // 'all'
    },
    [dragMode],
  );

  const clearAllIndicators = useCallback(() => {
    clearAllTreenodeIndicators();
  }, []);

  const clearDragState = useCallback(() => {
    clearAllIndicators();
    // 恢复所有 treenode 的透明度
    document.querySelectorAll(".ant-tree-treenode").forEach((el) => {
      (el as HTMLElement).style.opacity = "";
    });

    setDragState({
      dragNodeKey: null,
      dropNodeKey: null,
      dropPosition: 0,
    });
    setIsDragging(false);
    dragNodeRef.current = null;
    prevDropTargetRef.current = null;
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, [clearAllIndicators]);

  const onDragStart = useCallback(
    (e: React.DragEvent, node: DataNode) => {
      if (!dragMode) return;
      const nd = node as DataNode & { _isTempNode?: boolean };
      if (!canDragNode(nd)) {
        e.preventDefault();
        return;
      }

      dragNodeRef.current = node;
      setIsDragging(true);
      setDragState((prev) => ({
        ...prev,
        dragNodeKey: String(node.key),
      }));

      // 标记被拖拽的节点（在 treenode 上加 translucent）
      const treeNodeEl = getTreeNodeEl(e.currentTarget as HTMLElement);
      if (treeNodeEl) {
        treeNodeEl.style.opacity = "0.4";
      }

      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(node.key));
    },
    [dragMode, canDragNode],
  );

  const onDragEnd = useCallback(
    (_e: React.DragEvent) => {
      clearDragState();
    },
    [clearDragState],
  );

  const onItemDragEnter = useCallback(
    (e: React.DragEvent, node: DataNode) => {
      e.preventDefault();
      e.stopPropagation();

      if (!dragNodeRef.current) return;

      const nodeKey = String(node.key);
      const dragKey = String(dragNodeRef.current.key);

      // 不能拖到自己身上
      if (nodeKey === dragKey) return;

      // 自动展开收起状态的文件夹
      if (!node.isLeaf) {
        if (expandTimerRef.current) {
          clearTimeout(expandTimerRef.current);
        }
        expandTimerRef.current = setTimeout(() => {
          onDragEnterExpand?.(nodeKey);
        }, 800);
      }
    },
    [onDragEnterExpand],
  );

  const onItemDragOver = useCallback(
    (e: React.DragEvent, node: DataNode) => {
      e.preventDefault();
      e.stopPropagation();

      if (!dragNodeRef.current) return;

      const nodeKey = String(node.key);
      const dragKey = String(dragNodeRef.current.key);
      if (nodeKey === dragKey) return;

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const isFolder = !node.isLeaf;
      const { dropPosition } = calcDropPositionByRect(e.clientY, rect, isFolder);

      // 如果节点是文件且计算结果是 inside(0)，改为 after(1)
      const finalDropPosition = (!isFolder && dropPosition === 0 ? 1 : dropPosition) as DropPosition;

      const currentTreeNode = getTreeNodeEl(e.currentTarget as HTMLElement);
      if (!currentTreeNode) return;

      // 清除上一个节点的 indicator
      if (prevDropTargetRef.current && prevDropTargetRef.current !== nodeKey) {
        const prevTreeNode = document.querySelector(
          `.ant-tree-treenode [data-node-key="${prevDropTargetRef.current}"]`,
        )?.closest(".ant-tree-treenode");
        if (prevTreeNode) {
          prevTreeNode.classList.remove(...INDICATOR_CLASSES);
        }
      }
      prevDropTargetRef.current = nodeKey;

      // 在当前 treenode 上设置 indicator class
      currentTreeNode.classList.remove(...INDICATOR_CLASSES);
      const cls =
        finalDropPosition === -1
          ? INDICATOR_BEFORE_CLASS
          : finalDropPosition === 0
            ? INDICATOR_INSIDE_CLASS
            : INDICATOR_AFTER_CLASS;
      currentTreeNode.classList.add(cls);

      setDragState({
        dragNodeKey: dragKey,
        dropNodeKey: nodeKey,
        dropPosition: finalDropPosition,
      });

      e.dataTransfer.dropEffect = "move";
    },
    [],
  );

  const onItemDragLeave = useCallback((_e: React.DragEvent) => {
    // 不清除 dragState，因为可能只是进入子元素触发的 leave
  }, []);

  const onItemDrop = useCallback(
    (e: React.DragEvent, node: DataNode) => {
      e.preventDefault();
      e.stopPropagation();

      if (!dragNodeRef.current) return;

      const nodeKey = String(node.key);
      const dragKey = String(dragNodeRef.current.key);
      if (nodeKey === dragKey) {
        clearDragState();
        return;
      }

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const isFolder = !node.isLeaf;
      const { dropPosition } = calcDropPositionByRect(e.clientY, rect, isFolder);
      const finalDropPosition = (!isFolder && dropPosition === 0 ? 1 : dropPosition) as DropPosition;

      onDrop?.(dragNodeRef.current, node, finalDropPosition);
      clearDragState();
    },
    [onDrop, clearDragState],
  );

  // ====== 容器级别的事件处理（用于拖到空白区域/底部） ======

  /**
   * 获取容器内所有可见的 data-node-key 元素的 rect 列表，
   * 用于判断鼠标是否在最后一个节点下方
   */
  const getLastNodeRect = useCallback(
    (containerEl: HTMLElement): DOMRect | null => {
      const nodeEls = containerEl.querySelectorAll("[data-node-key]");
      if (nodeEls.length === 0) return null;
      const lastEl = nodeEls[nodeEls.length - 1];
      return lastEl.getBoundingClientRect();
    },
    [],
  );

  const onContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!dragNodeRef.current) return;

      const containerEl = e.currentTarget as HTMLElement;

      // 检查鼠标是否在某个节点上（通过判断 event target 是否在 data-node-key 元素内）
      const targetEl = e.target as HTMLElement;
      const isOnNode = targetEl.closest("[data-node-key]");

      if (isOnNode) {
        // 鼠标在节点上，由 onItemDragOver 处理，这里清除底部相关的 indicator
        if (prevDropTargetRef.current === "__bottom_placeholder__") {
          clearAllIndicators();
          prevDropTargetRef.current = null;
        }
        return;
      }

      // 鼠标在空白区域，检查是否在所有节点下方
      const lastRect = getLastNodeRect(containerEl);
      if (lastRect && e.clientY > lastRect.bottom) {
        e.preventDefault();
        e.stopPropagation();

        // 清除其他节点的 indicator
        if (prevDropTargetRef.current && prevDropTargetRef.current !== "__bottom_placeholder__") {
          const prevTreeNode = document.querySelector(
            `.ant-tree-treenode [data-node-key="${prevDropTargetRef.current}"]`,
          )?.closest(".ant-tree-treenode");
          if (prevTreeNode) {
            prevTreeNode.classList.remove(...INDICATOR_CLASSES);
          }
        }
        prevDropTargetRef.current = "__bottom_placeholder__";

        // 在最后一个 treenode 上显示 after 样式
        const lastNodeEls = containerEl.querySelectorAll(".ant-tree-treenode");
        const lastTreenode = lastNodeEls[lastNodeEls.length - 1] as HTMLElement | undefined;
        if (lastTreenode) {
          lastTreenode.classList.remove(...INDICATOR_CLASSES);
          lastTreenode.classList.add(INDICATOR_AFTER_CLASS);
        }

        setDragState({
          dragNodeKey: String(dragNodeRef.current.key),
          dropNodeKey: "__bottom_placeholder__",
          dropPosition: 1 as DropPosition,
        });

        e.dataTransfer.dropEffect = "move";
      }
    },
    [clearAllIndicators, getLastNodeRect],
  );

  const onContainerDrop = useCallback(
    (e: React.DragEvent) => {
      if (!dragNodeRef.current) {
        return;
      }

      const containerEl = e.currentTarget as HTMLElement;
      const targetEl = e.target as HTMLElement;
      const isOnNode = targetEl.closest("[data-node-key]");

      if (isOnNode) {
        // 鼠标在节点上，由 onItemDrop 处理
        return;
      }

      // 检查是否在最后一个节点下方
      const lastRect = getLastNodeRect(containerEl);
      if (lastRect && e.clientY > lastRect.bottom) {
        e.preventDefault();
        e.stopPropagation();

        // 拖到最底部：视为 insert after 最后一个根节点
        const placeholderNode: DataNode = {
          key: "__bottom_placeholder__",
          title: "",
          isLeaf: true,
        };
        onDrop?.(dragNodeRef.current, placeholderNode, 1 as DropPosition);
        clearDragState();
      }
    },
    [onDrop, clearDragState, getLastNodeRect],
  );

  return {
    dragState,
    onDragStart,
    onDragEnd,
    onItemDragEnter,
    onItemDragOver,
    onItemDragLeave,
    onItemDrop,
    onContainerDragOver,
    onContainerDrop,
    clearDragState,
    isDragging,
    canDragNode,
  };
}